import PageHeaderContainer from 'shared/components/PageHeader/container'
import React, { useCallback, useEffect, useState } from 'react'
import { AdminContainer } from '..'
import ContainerShell from 'shared/components/ContainerShell'
import ContainerShellIntro from 'shared/components/ContainerShellIntro'
import useModal from 'hooks/useModal'
import RoleTable from './RoleTable'
import useAxios from 'hooks/useAxios'
import { getAxios } from 'api'
import { toast } from 'libs/toast'
import ConfirmDeletionModal from 'components/Modals/ConfirmDeletionModal'
import { CrownBadge } from 'components/ProFeatureLabel'
import { LimitFeatureButton } from 'components/LimitFeatureButton'
import CreateOrUpdateRoleModal from './RoleModal'
import { EMPTY_ROLE_SETTINGS } from 'components/CustomRoleForm/constants'
import {
  getInitialDashboardPermissions,
  getInitialSurveyPermissions,
} from 'components/CustomRoleForm/utils'

const roleInitialState = EMPTY_ROLE_SETTINGS

const Role = ({
  roles,
  rolesDataLoading,
  hasManageRolePermission,
  isEnabledCustomRoles,
  checkingEnabledCustomRole,
}) => {
  const [rolesList, setEditingRoleList] = useState([])
  const [editingRole, setEditingRole] = useState(roleInitialState)
  const [deletingRoleId, setDeletingRoleId] = useState(null)
  const [isLoadingRole, setIsLoadingRole] = useState(false)

  const {
    Modal: RoleModal,
    open: openRoleModal,
    isOpen: isOpenRoleModal,
    close: closeRoleModal,
  } = useModal()

  const {
    Modal: DeletionModal,
    open: openDeletionModal,
    isOpen: isOpenDeletionModal,
    close: closeDeletionModal,
  } = useModal()

  const { fetch: deleteRole, loading: isDeletingRole } = useAxios({
    method: 'delete',
    skip: true,
  })

  const {
    fetch: fetchAllSurvey,
    data: allSurveys,
    loading: isFetchingSurveys,
  } = useAxios({
    url: 'account/survey/?limit=5000&order_by=asc',
  })

  const {
    fetch: fetchAllDashboard,
    data: allDashboards,
    loading: isFetchingDashboards,
  } = useAxios({
    url: 'dashboard/dashboards/?limit=100&order_by=asc',
  })

  function handleEdit(id, name, isPredefined) {
    openRoleModal()
    setEditingRole({
      ...roleInitialState,
      id: id,
      name: name,
      is_predefined: isPredefined,
    })
    setIsLoadingRole(true)
    getAxios()
      .get(`account/role/${id}/`)
      .then((res) => {
        if (res.data) {
          setEditingRole({ ...res.data, original_name: res.data.name })
          setIsLoadingRole(false)
        }
      })
      .catch((err) => {
        console.error(err)
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        setIsLoadingRole(false)
      })
  }

  function handleDelete(id) {
    setDeletingRoleId(id)
    openDeletionModal()
  }

  function handleDeleteRole() {
    deleteRole({ url: `account/roles/${deletingRoleId}/` })
      .then((res) => {
        if (res.status === 204) {
          toast.success('The role was successfully deleted.')
          setEditingRoleList((prev) => {
            const results = prev.filter((item) => item.id !== deletingRoleId)

            return results
          })
          closeDeletionModal()
        }
      })
      .catch((err) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        console.error(err)
      })
  }

  function resetInitialState() {
    setEditingRole(roleInitialState)
    updateSurveyAccessToRole(allSurveys?.results || [])
    updateDashboardAccessToRole(allDashboards?.results || [])
  }

  function handleCloseModal() {
    resetInitialState()
    closeRoleModal()
  }

  const updateSurveyAccessToRole = useCallback((allSurveys) => {
    setEditingRole((prev) => {
      return {
        ...prev,
        survey_access: getInitialSurveyPermissions(allSurveys),
      }
    })
  }, [])

  const updateDashboardAccessToRole = useCallback((allDashboards) => {
    setEditingRole((prev) => {
      return {
        ...prev,
        dashboard_access: getInitialDashboardPermissions(allDashboards),
      }
    })
  }, [])

  useEffect(() => {
    if (roles) {
      const roleOrder = ['owner', 'admin', 'manager', 'collaborator', 'billing']
      const sortedDefaultRole = roles
        .filter((item) => {
          return item.is_predefined == true
        })
        .sort((a, b) => roleOrder.indexOf(a.name) - roleOrder.indexOf(b.name))

      const sortedCustomRole = roles
        .filter((item) => {
          return item.is_predefined == false
        })
        .sort((a, b) => a.name.localeCompare(b.name))

      setEditingRoleList([...sortedDefaultRole, ...sortedCustomRole])
    }
  }, [roles])

  useEffect(() => {
    fetchAllSurvey().then((response) => {
      updateSurveyAccessToRole(response.data.results)
    })
  }, [fetchAllSurvey, updateSurveyAccessToRole])

  useEffect(() => {
    fetchAllDashboard().then((response) => {
      updateDashboardAccessToRole(response.data.results)
    })
  }, [fetchAllDashboard, updateDashboardAccessToRole])

  return (
    <React.Fragment>
      <PageHeaderContainer
        title="Roles"
        rightSlot={
          hasManageRolePermission && (
            <LimitFeatureButton
              isAllowedFeature={isEnabledCustomRoles}
              handleClick={openRoleModal}
              descriptionTitle={
                <span className="flex items-center">
                  Custom roles <CrownBadge />
                </span>
              }
              descriptionContent={
                'Custom roles are available on a higher plan. Upgrade to unlock this feature.'
              }
              learnMoreLink={
                'https://help.simplesat.io/en/articles/2361167-users-permissions#custom-roles'
              }
              buttonText={'New role'}
              loading={checkingEnabledCustomRole}
            />
          )
        }
      />
      <AdminContainer>
        <ContainerShell
          isLoading={rolesDataLoading}
          intro={
            <ContainerShellIntro
              helpLinkLabel="Learn more about user roles and permissions"
              helpLinkUrl="https://help.simplesat.io/en/articles/2361167-user-roles-and-permissions"
              title="Roles"
              tooltip={
                'Roles are sets of permissions that control what users can do in Simplesat. ' +
                'Assign roles to users to match their responsibilities, or create custom roles for specific needs.'
              }
            />
          }
        >
          <RoleTable
            hasManageRolePermission={hasManageRolePermission}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            dataSource={rolesList}
          />
        </ContainerShell>
      </AdminContainer>
      {isOpenRoleModal && (
        <CreateOrUpdateRoleModal
          close={handleCloseModal}
          editingRole={editingRole}
          isOpenRoleModal={isOpenRoleModal}
          modal={RoleModal}
          roles={rolesList}
          setEditingRole={setEditingRole}
          setEditingRoleList={setEditingRoleList}
          isFetchingSurveys={isFetchingSurveys}
          isFetchingDashboards={isFetchingDashboards}
          hasManageRolePermission={hasManageRolePermission}
          loading={isLoadingRole}
        />
      )}

      {isOpenDeletionModal && (
        <ConfirmDeletionModal
          modal={DeletionModal}
          title="Delete role"
          loading={isDeletingRole}
          description="Are you sure you want to delete this role? Any users assigned to this role will be automatically reassigned to the Collaborator role."
          buttonLabel="Delete role"
          close={closeDeletionModal}
          handleDelete={handleDeleteRole}
        />
      )}
    </React.Fragment>
  )
}

export default Role



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/Roles/Role.js